/**
 * Tax calculation utilities for Phieu Thanh Toan Tam Ung module
 *
 * This module provides pure functions for tax calculations following functional programming principles.
 * All functions are side-effect free and can be easily tested and reused.
 */

/**
 * Calculate tax amount based on base amount and tax rate
 *
 * @param baseAmount - The base amount (Tiền hàng VND)
 * @param taxRate - The tax rate percentage (Thuế suất)
 * @returns The calculated tax amount rounded to 2 decimal places
 *
 * @example
 * calculateTaxAmount(12, 5) // returns 0.6 (12 * 5% = 0.6)
 * calculateTaxAmount(100, 10) // returns 10 (100 * 10% = 10)
 */
export const calculateTaxAmount = (baseAmount: number | string, taxRate: number | string): number => {
  const amount = Number(baseAmount) || 0;
  const rate = Number(taxRate) || 0;

  // Handle edge cases
  if (amount < 0 || rate < 0) {
    return 0;
  }

  // Calculate tax amount and round to 2 decimal places
  return Math.round(((amount * rate) / 100) * 100) / 100;
};

/**
 * Calculate total amount including tax
 *
 * @param baseAmount - The base amount (Tiền hàng VND)
 * @param taxAmount - The tax amount (Thuế VND)
 * @returns The total amount (base + tax)
 */
export const calculateTotalAmount = (baseAmount: number | string, taxAmount: number | string): number => {
  const base = Number(baseAmount) || 0;
  const tax = Number(taxAmount) || 0;

  return Math.round((base + tax) * 100) / 100;
};

/**
 * Calculate tax rate from base amount and tax amount
 *
 * @param baseAmount - The base amount (Tiền hàng VND)
 * @param taxAmount - The tax amount (Thuế VND)
 * @returns The calculated tax rate percentage
 */
export const calculateTaxRate = (baseAmount: number | string, taxAmount: number | string): number => {
  const base = Number(baseAmount) || 0;
  const tax = Number(taxAmount) || 0;

  if (base === 0) {
    return 0;
  }

  return Math.round((tax / base) * 100 * 100) / 100;
};

/**
 * Validate tax calculation inputs
 *
 * @param baseAmount - The base amount to validate
 * @param taxRate - The tax rate to validate
 * @returns Object with validation result and error message if MuaHangRecord
 */
export const validateTaxInputs = (baseAmount: number | string, taxRate: number | string) => {
  const amount = Number(baseAmount);
  const rate = Number(taxRate);

  if (isNaN(amount) || isNaN(rate)) {
    return {
      isValid: false,
      error: 'Invalid input: amounts and rates must be numbers'
    };
  }

  if (amount < 0) {
    return {
      isValid: false,
      error: 'Base amount cannot be negative'
    };
  }

  if (rate < 0 || rate > 100) {
    return {
      isValid: false,
      error: 'Tax rate must be between 0 and 100 percent'
    };
  }

  return {
    isValid: true,
    error: null
  };
};

/**
 * Format tax amount for display
 *
 * @param amount - The amount to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted amount string
 */
export const formatTaxAmount = (amount: number | string, decimals: number = 2): string => {
  const num = Number(amount) || 0;
  return num.toFixed(decimals);
};
